<template>
  <RevSkeleton
    v-if="!isClientSide"
    class="rounded-lg"
    height="234px"
    shape="rectangle"
    width="100%"
  />

  <div
    v-else
    class="bg-surface-brand-min rounded-lg overflow-hidden p-24 lg:p-0"
    :class="{
      'lg:px-72': consentGiven,
      'lg:pl-[112px]': !consentGiven,
    }"
    data-test="app-store-download-banner-large"
  >
    <div
      class="flex"
      :class="{
        'h-full lg:min-h-[152px] lg:justify-between': !consentGiven,
        'justify-center gap-40': consentGiven,
      }"
    >
      <RevIllustration
        v-if="consentGiven && qrCodePath"
        :alt="i18n(translations.downloadAppQrCodeImage)"
        class="hidden pt-24 lg:block"
        data-test="app-store-download-banner-large-qr-code"
        :height="164"
        :src="qrCodePath"
        :width="200"
      />

      <div
        class="flex flex-col justify-center"
        :class="{ 'w-full': !consentGiven }"
      >
        <div class="flex justify-center lg:justify-start">
          <RevIllustration
            :alt="i18n(translations.downloadAppBrandImage)"
            class="mr-10 !size-40 lg:hidden"
            :height="40"
            src="/img/lead-collection/appDownload/bm-logo-dot-black.svg"
            :width="40"
          />

          <p v-if="title" class="heading-3 self-center lg:heading-2">
            {{ i18n(title) }}
          </p>
        </div>

        <div
          class="flex flex-col-reverse items-center lg:flex-col lg:items-start"
        >
          <div class="mt-8 flex items-center lg:mt-0">
            <RevRating v-if="rating" :score="rating" size="small" />

            <p class="caption">
              <span class="px-8">•</span>{{ i18n(translations.ratingMessage) }}
            </p>
          </div>

          <AppStoreButtons
            :apple-store-tracking-name="`${routeName}_new_app_store`"
            :apple-store-url="desktopTrackedUrl"
            class="mt-16 flex-row-reverse"
            :class="{
              'hidden lg:flex': desktopTrackedUrl && consentGiven,
            }"
            :play-store-tracking-name="`${routeName}_new_google_play`"
            :play-store-url="desktopTrackedUrl"
            :show-in-all-countries="true"
            trackingZone="app_download_new_notification"
          />

          <RevButton
            v-if="mobileTrackedUrl && consentGiven"
            class="mt-16 lg:hidden"
            data-test="app-store-download-banner-large-single-button"
            full-width="always"
            target="_blank"
            :to="mobileTrackedUrl"
            :tracking="{
              name: `${routeName}_new_tracked_store`,
              url: mobileTrackedUrl,
              zone: 'app_download_new_notification',
            }"
            variant="primary"
          >
            {{ i18n(translations.downloadApp) }}
          </RevButton>
        </div>
      </div>

      <RevIllustration
        v-if="!consentGiven"
        :alt="i18n(translations.downloadAppGenericPhoneImage)"
        class="hidden lg:block"
        data-test="app-store-download-banner-large-generic-phone"
        :height="152"
        src="/img/lead-collection/appDownload/app-download-generic.png"
        :width="220"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useRoute } from '#imports'
import { computed, onMounted, ref } from 'vue'

import AppStoreButtons from '@backmarket/nuxt-layer-base/components/AppStoreButtons/AppStoreButtons.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'
import { RevIllustration } from '@ds/components/Illustration'
import { RevRating } from '@ds/components/Rating'
import { RevSkeleton } from '@ds/components/Skeleton'

import translations from '../AppStoreDownloadBanner.translations'
import type { BannerVariantProps } from '../AppStoreDownloadBanner.types'

const i18n = useI18n()
const route = useRoute()

const {
  consentGiven = false,
  desktopTrackedUrl,
  mobileTrackedUrl,
} = defineProps<BannerVariantProps>()

const routeName = computed(() => route.name?.toString() || '')

const isClientSide = ref(false)

onMounted(() => {
  isClientSide.value = true
})
</script>
